// The API types `imageUrl` as `string | null`, but that's not a runtime
// guarantee — coerce anything else (a stray number/object from a bad
// response, an empty/whitespace string) down to '' so callers can safely
// treat it as "no image" instead of passing garbage into an <img src>.
export const toSafeImageUrl = (value: unknown): string =>
  typeof value === 'string' && value.trim() ? value.trim() : '';

export const VALIDATION_MESSAGES = {
  STORE_NAME_REQUIRED: 'Store Name is required',
  MERCHANT_REQUIRED: 'Merchant is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  PREP_TIME_INVALID: 'Please enter a valid preparation time in minutes',
  TAX_RATE_INVALID: 'Tax rate must be between 0 and 100',
  TAX_RATE_REQUIRED_WHEN_TAXABLE: 'Tax rate must be greater than 0 when tax is enabled',
  TAX_RATE_TOO_PRECISE: 'Tax rate can have at most 3 decimal places',
} as const;

export const PLACEHOLDERS = {
  STORE_NAME: 'Enter store name (e.g., Downtown Location)',
  SELECT_MERCHANT: 'Select a merchant',
  SEARCH_STORES: 'Search by store name, address, or city',
  ALL_MERCHANTS: 'All Merchants',
  PREP_TIME: 'Enter estimated preparation time in minutes (e.g., 15)',
  TAX_RATE: 'Enter tax percentage, 0–100, up to 3 decimals (e.g., 8.875)',
} as const;

export const MESSAGES = {
  STORE_CREATED: 'Store Created Successfully! 🎉',
  STORE_UPDATED: 'Store Updated Successfully! 🚀',
  STORE_DELETED: 'Store Deleted Successfully! 🗑️',
  DELETE_CONFIRMATION: 'Are you sure you want to delete this store? This action cannot be undone and will permanently remove all associated data.',
  NO_STORES: 'No stores found',
  GET_STARTED: 'Get started by creating your first store location',
  GET_STARTED_ADMIN: 'Create a store and assign it to a merchant to get started',
  MERCHANT_ACTION_DISABLED: 'Only Admin users can create, update, or delete stores.',
  AUTO_ACCEPT_ACTION_DISABLED: 'Only Admin or Merchant users can change this setting.',
  AUTO_ACCEPT_HELPER: 'Paid orders go to Preparing without kitchen accept.',
  TAXABLE_HELPER: 'When off, every order at this store is taxed $0 — item-level tax settings cannot override this.',
  AUTO_ACCEPT_UPDATED: 'Auto-accept setting updated',
  BULK_AUTO_ACCEPT_UPDATED: 'Auto-accept setting updated for all locations',
  SELECT_MERCHANT_FOR_BULK: 'Choose a merchant from the filter above to apply this to all of their locations.',
} as const;

const pluralStores = (count: number) => `${count} store${count === 1 ? '' : 's'}`;

// Bulk actions dropdown + confirm dialog copy, generated from the affected
// store count so it always matches what's about to change.
export const BULK_AUTO_ACCEPT_COPY = {
  affectsLabel: (count: number) => `Affects all ${pluralStores(count)}`,
  confirmTitle: (enable: boolean) => `${enable ? 'Enable' : 'Disable'} auto-accept orders for all stores?`,
  confirmDescription: (enable: boolean, count: number) =>
    enable
      ? `This will turn on auto-accept orders for all ${pluralStores(count)}. Paid orders will go directly to preparing without manual accept.`
      : `This will turn off auto-accept orders for all ${pluralStores(count)}. Staff will need to manually accept paid orders.`,
  confirmButtonLabel: (enable: boolean) => `Yes, ${enable ? 'enable' : 'disable'} for all`,
} as const;

// Page copy differs by who's looking: admins manage stores across every
// merchant, merchants/managers only ever see and create their own.
export const SUBTITLES = {
  ADMIN: 'View and manage store locations across every merchant, or create a new store on behalf of one.',
  MERCHANT: 'Manage all your store locations, add new ones, edit existing details, and organize your business operations efficiently.',
} as const;

export const IMAGE_FILE_HINT = 'File requirement: JPG, PNG, GIF, or WEBP up to 10 MB.';

export const LABELS = {
  STORE_NAME: 'Store Name',
  STORE_IMAGE: 'Store Image',
  MERCHANT: 'Select Merchant',
  STORE_ADDRESS: 'Store Address',
  PREP_TIME: 'Estimated Preparation Time (minutes)',
  AUTO_ACCEPT_ORDERS: 'Auto-accept orders',
  STORE_TAXABLE: 'Store tax enabled',
  STORE_TAX_RATE: 'Tax Rate (%)',
  BULK_ACTIONS: 'Bulk actions',
  BULK_ENABLE_TITLE: 'Enable auto-accept orders',
  BULK_ENABLE_SUBTITLE: 'Turn on for all locations',
  BULK_DISABLE_TITLE: 'Disable auto-accept orders',
  BULK_DISABLE_SUBTITLE: 'Turn off for all locations',
  CREATE_STORE: 'Create Store',
  UPDATE_STORE: 'Update Store',
  DELETE_STORE: 'Delete Store',
  EDIT_STORE: 'Edit Store',
  CANCEL: 'Cancel',
  SAVE: 'Save Changes',
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
  REFRESH: 'Refresh',
  LAST_UPDATED: 'Last Updated',
  TOTAL_STORES: 'Total Stores',
  WITH_ADDRESS: 'With Address',
  STORES_MANAGEMENT: 'Stores Management',
} as const;

// Matches the Items tab's image constraints (Menu section) — same 10MB cap
// and file types, so store images follow the same rules editors already
// know from that form.
export const FILE_CONSTRAINTS = {
  MAX_IMAGE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
} as const;